import { CmsSchema } from '@/cms/content';
import { serializeCmsJson } from '@workspace/url-source/cms';
import { useCmsFaq } from '@/components/cms/useCmsFaq';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
/**
 * Zamówienie specjalne — /zamowienie-specjalne
 * Formularz krokowy + pełna strona edytorialna.
 * Backend nie podłączony — struktura danych gotowa do integracji.
 */
import { useState, useRef, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from '@/lib/useInView';
import {
  ArrowRight, ArrowLeft, Upload, X,
  CheckCircle2, Flower2, Leaf, Gift, Sparkles, Layers, Heart,
  ChevronRight,
} from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { CategoryBuyingGuide } from '@/components/CategoryBuyingGuide';
import { REMAINING_SEO_GUIDES } from '@/data/remainingSeoContent';
import { SectionLabel } from '@/components/ui/SectionLabel';
import { FaqAccordionItem } from '@/components/FaqAccordionItem';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { isValidInternationalPhone, phoneValidationMessage } from '@/lib/phone';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { buildFaqJsonLd } from '@/lib/faqJsonLd';
import { ZAMOWIENIE_SPECJALNE_CONTENT } from '@/data/zamowienieSpecjalneContent';

/* ── Tokens ─────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.50)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const CREAM3 = '#fff';
const BROWN  = '#5a3e28';


/* ══════════════════════════════════════════════════════════
   Data structure — gotowa do podłączenia do backendu
══════════════════════════════════════════════════════════ */
export type OrderStatus =
  | 'Nowe' | 'W trakcie' | 'Czekamy na informacje'
  | 'Oferta przedstawiona' | 'Zaakceptowane' | 'Odrzucone'
  | 'Zrealizowane' | 'Zamknięte';

export interface SpecialOrderPayload {
  /* meta — uzupełniane przez backend */
  id?:        string;
  createdAt?: string;
  status?:    OrderStatus;

  /* krok 1 */
  description:       string;
  orderType:         string;
  inspirationImage?: File | null;

  /* krok 2 */
  preferredFlowers?: string;
  preferredColors?:  string;
  preferredStyle?:   string;
  budget?:           string;
  quantity?:         string;

  /* krok 3 */
  neededBy?:      string;
  additionalInfo?: string;

  /* krok 4 */
  fullName: string;
  phone:    string;
  email:    string;
}

const EMPTY: SpecialOrderPayload = {
  description: '', orderType: '',
  inspirationImage: null,
  preferredFlowers: '', preferredColors: '', preferredStyle: '',
  budget: '', quantity: '',
  neededBy: '', additionalInfo: '',
  fullName: '', phone: '', email: '',
};

const STEPS = ZAMOWIENIE_SPECJALNE_CONTENT.formSteps;
const ORDER_TYPES = ZAMOWIENIE_SPECJALNE_CONTENT.orderTypes;

const CATEGORY_ICON_MAP: Record<string, LucideIcon> = {
  flower2: Flower2, heart: Heart, leaf: Leaf, layers: Layers, gift: Gift, sparkles: Sparkles,
};

/* ── Shared ─────────────────────────────────────────────── */
function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontSize: 11.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>
      {children}{required && <span style={{ color: GOLD }}> *</span>}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  fontFamily: 'DM Sans, sans-serif', fontSize: 14.5, color: DARK,
  background: '#fff', border: `1px solid ${LINE}`,
  padding: '13px 16px', outline: 'none',
  transition: 'border-color 0.2s',
};

function Input({ style, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  const [focus, setFocus] = useState(false);
  return (
    <input
      {...props}
      style={{ ...inputStyle, borderColor: focus ? GOLD : LINE, ...style }}
      onFocus={e => { setFocus(true); props.onFocus?.(e); }}
      onBlur={e => { setFocus(false); props.onBlur?.(e); }}
    />
  );
}

function Textarea({ style, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const [focus, setFocus] = useState(false);
  return (
    <textarea
      {...props}
      style={{ ...inputStyle, resize: 'vertical', minHeight: 110, borderColor: focus ? GOLD : LINE, ...style }}
      onFocus={e => { setFocus(true); props.onFocus?.(e); }}
      onBlur={e => { setFocus(false); props.onBlur?.(e); }}
    />
  );
}

function Select({ style, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const [focus, setFocus] = useState(false);
  return (
    <select
      {...props}
      style={{ ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7' viewBox='0 0 12 7'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23c4a35a' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: 36, borderColor: focus ? GOLD : LINE, ...style }}
      onFocus={e => { setFocus(true); props.onFocus?.(e); }}
      onBlur={e => { setFocus(false); props.onBlur?.(e); }}
    />
  );
}

/* ── Image upload ────────────────────────────────────────── */
function ImageUpload({ value, onChange }: { value: File | null; onChange: (f: File | null) => void }) {
  const inputRef  = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const accept = (file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)/)) return;
    onChange(file);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const remove = () => { onChange(null); setPreview(null); };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDrag(false);
    const file = e.dataTransfer.files[0];
    if (file) accept(file);
  }, []);

  if (preview && value) {
    return (
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16/7', overflow: 'hidden', background: '#fff' }}>
        <img src={preview} alt={ZAMOWIENIE_SPECJALNE_CONTENT.form.uploadPreviewAlt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        <button
          type="button" onClick={remove} aria-label={ZAMOWIENIE_SPECJALNE_CONTENT.form.uploadRemoveAriaLabel}
          style={{ position: 'absolute', top: 12, right: 12, width: 32, height: 32, borderRadius: '50%', background: 'rgba(42,31,20,0.7)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <X size={14} color="#fff" />
        </button>
        <div style={{ position: 'absolute', bottom: 12, left: 12, background: 'rgba(42,31,20,0.65)', padding: '4px 10px' }}>
          <p style={{ fontFamily: 'DM Sans', fontSize: 11, color: 'rgba(255,255,255,0.8)', margin: 0 }}>{value.name}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) accept(f); }} />
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        style={{
          border: `1px dashed ${drag ? GOLD : `${GOLD}55`}`,
          background: drag ? `${GOLD}08` : CREAM2,
          padding: 'clamp(28px,4vw,48px)',
          cursor: 'pointer', textAlign: 'center',
          transition: 'background 0.2s, border-color 0.2s',
        }}
      >
        <Upload size={22} color={`${GOLD}88`} style={{ margin: '0 auto 12px' }} />
        <p style={{ fontFamily: 'DM Sans', fontSize: 14.5, color: DARK, margin: '0 0 6px', fontWeight: 500 }}>
          {ZAMOWIENIE_SPECJALNE_CONTENT.form.uploadTitle}
        </p>
        <p style={{ fontFamily: 'DM Sans', fontSize: 12, color: MUTED, margin: 0 }}>
          {ZAMOWIENIE_SPECJALNE_CONTENT.form.uploadHint} <span style={{ color: GOLD }}>{ZAMOWIENIE_SPECJALNE_CONTENT.form.uploadHintLink}</span> {ZAMOWIENIE_SPECJALNE_CONTENT.form.uploadHintFormats}
        </p>
      </div>
    </>
  );
}

/* ── Progress bar ────────────────────────────────────────── */
function ProgressBar({ step }: { step: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 'clamp(32px,4vw,48px)', overflowX: 'auto', paddingBottom: 4 }}>
      {STEPS.map((label, i) => {
        const done    = i < step;
        const current = i === step;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', border: `1.5px solid ${done || current ? GOLD : LINE}`, background: done ? GOLD : current ? `${GOLD}14` : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s' }}>
                {done
                  ? <CheckCircle2 size={13} color="#fff" />
                  : <span style={{ fontFamily: 'DM Sans', fontSize: 10.5, color: current ? GOLD : MUTED, fontWeight: current ? 600 : 400 }}>{i + 1}</span>
                }
              </div>
              <span style={{ fontFamily: 'DM Sans', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: current ? DARK : done ? GOLD : MUTED, whiteSpace: 'nowrap', fontWeight: current ? 600 : 400 }}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1, background: i < step ? GOLD : LINE, margin: '0 6px', marginBottom: 22, transition: 'background 0.4s' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Form steps ──────────────────────────────────────────── */
function Step1({ data, update }: { data: SpecialOrderPayload; update: (k: keyof SpecialOrderPayload, v: any) => void }) {
  return (
    <motion.div key="step1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
      <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.5rem,2.5vw,2rem)', fontWeight: 400, color: DARK, margin: '0 0 28px', lineHeight: 1.2 }}>
        {ZAMOWIENIE_SPECJALNE_CONTENT.form.step1Heading}
      </h3>

      <div style={{ marginBottom: 22 }}>
        <Label required>{ZAMOWIENIE_SPECJALNE_CONTENT.form.descriptionLabel}</Label>
        <Textarea
          value={data.description}
          onChange={e => update('description', e.target.value)}
          placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.descriptionPlaceholder}
          rows={5}
          required
        />
      </div>

      <div style={{ marginBottom: 28 }}>
        <Label required>{ZAMOWIENIE_SPECJALNE_CONTENT.form.orderTypeLabel}</Label>
        <Select value={data.orderType} onChange={e => update('orderType', e.target.value)} required>
          <option value="">{ZAMOWIENIE_SPECJALNE_CONTENT.form.orderTypePlaceholder}</option>
          {ORDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </Select>
      </div>

      <div>
        <Label>{ZAMOWIENIE_SPECJALNE_CONTENT.form.inspirationImageLabel} <span style={{ color: MUTED, fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>{ZAMOWIENIE_SPECJALNE_CONTENT.form.optionalSuffix}</span></Label>
        <ImageUpload value={data.inspirationImage ?? null} onChange={f => update('inspirationImage', f)} />
        <p style={{ fontFamily: 'DM Sans', fontSize: 12, color: MUTED, margin: '10px 0 0', lineHeight: 1.65 }}>
          {ZAMOWIENIE_SPECJALNE_CONTENT.form.inspirationImageNote}
        </p>
      </div>
    </motion.div>
  );
}

function Step2({ data, update }: { data: SpecialOrderPayload; update: (k: keyof SpecialOrderPayload, v: any) => void }) {
  return (
    <motion.div key="step2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
      <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.5rem,2.5vw,2rem)', fontWeight: 400, color: DARK, margin: '0 0 8px', lineHeight: 1.2 }}>
        {ZAMOWIENIE_SPECJALNE_CONTENT.form.step2Heading}
      </h3>
      <p style={{ fontFamily: 'DM Sans', fontSize: 13.5, color: MUTED, margin: '0 0 28px', lineHeight: 1.7 }}>
        {ZAMOWIENIE_SPECJALNE_CONTENT.form.step2Body}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: 18, marginBottom: 18 }}>
        <div>
          <Label>{ZAMOWIENIE_SPECJALNE_CONTENT.form.preferredFlowersLabel}</Label>
          <Input value={data.preferredFlowers ?? ''} onChange={e => update('preferredFlowers', e.target.value)} placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.preferredFlowersPlaceholder} />
        </div>
        <div>
          <Label>{ZAMOWIENIE_SPECJALNE_CONTENT.form.preferredColorsLabel}</Label>
          <Input value={data.preferredColors ?? ''} onChange={e => update('preferredColors', e.target.value)} placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.preferredColorsPlaceholder} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: 18, marginBottom: 18 }}>
        <div>
          <Label>{ZAMOWIENIE_SPECJALNE_CONTENT.form.preferredStyleLabel}</Label>
          <Input value={data.preferredStyle ?? ''} onChange={e => update('preferredStyle', e.target.value)} placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.preferredStylePlaceholder} />
        </div>
        <div>
          <Label>{ZAMOWIENIE_SPECJALNE_CONTENT.form.budgetLabel}</Label>
          <Input value={data.budget ?? ''} onChange={e => update('budget', e.target.value)} placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.budgetPlaceholder} />
        </div>
      </div>

      <div>
        <Label>{ZAMOWIENIE_SPECJALNE_CONTENT.form.quantityLabel}</Label>
        <Input value={data.quantity ?? ''} onChange={e => update('quantity', e.target.value)} placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.quantityPlaceholder} style={{ maxWidth: 360 }} />
      </div>
    </motion.div>
  );
}

function Step3({ data, update }: { data: SpecialOrderPayload; update: (k: keyof SpecialOrderPayload, v: any) => void }) {
  return (
    <motion.div key="step3" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
      <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.5rem,2.5vw,2rem)', fontWeight: 400, color: DARK, margin: '0 0 28px', lineHeight: 1.2 }}>
        {ZAMOWIENIE_SPECJALNE_CONTENT.form.step3Heading}
      </h3>

      <div style={{ marginBottom: 18 }}>
        <Label>{ZAMOWIENIE_SPECJALNE_CONTENT.form.neededByLabel}</Label>
        <Input type="date" value={data.neededBy ?? ''} onChange={e => update('neededBy', e.target.value)} min={new Date().toISOString().split('T')[0]} style={{ maxWidth: 280 }} />
      </div>

      <div style={{ background: '#fff', borderLeft: `2px solid ${GOLD}`, padding: '14px 18px', marginBottom: 28 }}>
        <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, lineHeight: 1.75, margin: 0 }}>
          {ZAMOWIENIE_SPECJALNE_CONTENT.form.neededByNote}
        </p>
      </div>

      <div>
        <Label>{ZAMOWIENIE_SPECJALNE_CONTENT.form.additionalInfoLabel} <span style={{ color: MUTED, fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>{ZAMOWIENIE_SPECJALNE_CONTENT.form.optionalSuffix}</span></Label>
        <Textarea
          value={data.additionalInfo ?? ''}
          onChange={e => update('additionalInfo', e.target.value)}
          placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.additionalInfoPlaceholder}
          rows={4}
        />
      </div>
    </motion.div>
  );
}

function Step4({ data, update }: { data: SpecialOrderPayload; update: (k: keyof SpecialOrderPayload, v: any) => void }) {
  return (
    <motion.div key="step4" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
      <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.5rem,2.5vw,2rem)', fontWeight: 400, color: DARK, margin: '0 0 28px', lineHeight: 1.2 }}>
        {ZAMOWIENIE_SPECJALNE_CONTENT.form.step4Heading}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,240px),1fr))', gap: 18, marginBottom: 18 }}>
        <div>
          <Label required>{ZAMOWIENIE_SPECJALNE_CONTENT.form.fullNameLabel}</Label>
          <Input value={data.fullName} onChange={e => update('fullName', e.target.value)} placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.fullNamePlaceholder} required />
        </div>
        <div>
          <Label required>{ZAMOWIENIE_SPECJALNE_CONTENT.form.phoneLabel}</Label>
          <PhoneInput value={data.phone} onChange={value => update('phone', value)} placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.phonePlaceholder} required
            error={data.phone ? phoneValidationMessage(data.phone, true) ?? undefined : undefined}
            style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Label required>{ZAMOWIENIE_SPECJALNE_CONTENT.form.emailLabel}</Label>
        <Input type="email" value={data.email} onChange={e => update('email', e.target.value)} placeholder={ZAMOWIENIE_SPECJALNE_CONTENT.form.emailPlaceholder} required />
      </div>

      <div style={{ background: '#fff', padding: '14px 18px', borderLeft: `2px solid ${GOLD}` }}>
        <p style={{ fontFamily: 'DM Sans', fontSize: 12.5, color: MUTED, lineHeight: 1.75, margin: 0 }}>
          {ZAMOWIENIE_SPECJALNE_CONTENT.form.contactNote}
        </p>
      </div>
    </motion.div>
  );
}

/* ── Success screen ──────────────────────────────────────── */
function SuccessScreen({ onReset }: { onReset: () => void }) {
  return (
    <motion.div key="success" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ textAlign: 'center', padding: 'clamp(40px,5vw,64px) 0' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', border: `1.5px solid ${GOLD}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
        <CheckCircle2 size={26} color={GOLD} />
      </div>
      <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem,3vw,2.6rem)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.15 }}>
        {ZAMOWIENIE_SPECJALNE_CONTENT.form.successHeadingLine1}<br />{ZAMOWIENIE_SPECJALNE_CONTENT.form.successHeadingLine2}
      </h3>
      <p style={{ fontFamily: 'DM Sans', fontSize: 14.5, color: MUTED, lineHeight: 1.85, margin: '0 0 32px', maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
        {ZAMOWIENIE_SPECJALNE_CONTENT.form.successBody}
      </p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '13px 28px', textDecoration: 'none' }}>
          {ZAMOWIENIE_SPECJALNE_CONTENT.form.backToShopLabel}
        </Link>
        <button onClick={onReset} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.22)`, background: 'none', padding: '13px 28px', cursor: 'pointer' }}>
          {ZAMOWIENIE_SPECJALNE_CONTENT.form.sendAnotherLabel}
        </button>
      </div>
    </motion.div>
  );
}

/* ── Multi-step form ─────────────────────────────────────── */
function SpecialOrderForm() {
  const [step, setStep]       = useState(0);
  const [data, setData]       = useState<SpecialOrderPayload>({ ...EMPTY });
  const [submitted, setSubmitted] = useState(false);

  const update = (k: keyof SpecialOrderPayload, v: any) =>
    setData(d => ({ ...d, [k]: v }));

  const canNext = () => {
    if (step === 0) return data.description.trim().length > 0 && data.orderType.length > 0;
    if (step === 3) return data.fullName.trim().length > 0 && isValidInternationalPhone(data.phone) && data.email.trim().length > 0;
    return true;
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canNext()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
      const res = await fetch(`${BASE}/api/special-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description:      data.description,
          orderType:        data.orderType,
          hasImage:         data.inspirationImage ? 'true' : 'false',
          preferredFlowers: data.preferredFlowers ?? '',
          preferredColors:  data.preferredColors ?? '',
          preferredStyle:   data.preferredStyle ?? '',
          budget:           data.budget ?? '',
          quantity:         data.quantity ?? '',
          neededBy:         data.neededBy ?? '',
          additionalInfo:   data.additionalInfo ?? '',
          fullName:         data.fullName,
          phone:            data.phone,
          email:            data.email,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? ZAMOWIENIE_SPECJALNE_CONTENT.form.genericServerErrorMessage);
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err.message ?? ZAMOWIENIE_SPECJALNE_CONTENT.form.genericSubmitErrorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setData({ ...EMPTY }); setStep(0); setSubmitted(false); };

  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, padding: 'clamp(28px,4vw,52px)' }}>
      {submitted ? (
        <SuccessScreen onReset={reset} />
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <ProgressBar step={step} />

          <AnimatePresence mode="wait">
            {step === 0 && <Step1 key="s1" data={data} update={update} />}
            {step === 1 && <Step2 key="s2" data={data} update={update} />}
            {step === 2 && <Step3 key="s3" data={data} update={update} />}
            {step === 3 && <Step4 key="s4" data={data} update={update} />}
          </AnimatePresence>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 36, paddingTop: 24, borderTop: `1px solid ${LINE}` }}>
            {step > 0 ? (
              <button type="button" onClick={() => setStep(s => s - 1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: MUTED, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <ArrowLeft size={13} /> {ZAMOWIENIE_SPECJALNE_CONTENT.form.backLabel}
              </button>
            ) : <span />}

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={() => { if (canNext()) setStep(s => s + 1); }}
                disabled={!canNext()}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: canNext() ? DARK : `${DARK}55`, padding: '13px 28px', border: 'none', cursor: canNext() ? 'pointer' : 'default' }}
              >
                {ZAMOWIENIE_SPECJALNE_CONTENT.form.nextLabel} <ArrowRight size={13} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!canNext() || submitting}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: (canNext() && !submitting) ? DARK : `${DARK}55`, padding: '13px 32px', border: 'none', cursor: (canNext() && !submitting) ? 'pointer' : 'default' }}
              >
                {submitting ? ZAMOWIENIE_SPECJALNE_CONTENT.form.submitLabelSending : ZAMOWIENIE_SPECJALNE_CONTENT.form.submitLabel} {!submitting && <ArrowRight size={13} />}
              </button>
            )}
          </div>
          {submitError && (
            <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: '#b91c1c', margin: '12px 0 0', lineHeight: 1.6 }}>{submitError}</p>
          )}
        </form>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   PAGE
════════════════════════════════════════════════════════════ */
export default function ZamowienieSpecjalnePage() {
  usePrerenderReady();
  useSeo(PAGE_SEO.zamowienieSpecjalne);
  const formRef = useRef<HTMLElement>(null);
  const { ref: refS1, inView: inS1 } = useInView<HTMLElement>();
  const { ref: refS2, inView: inS2 } = useInView<HTMLElement>();
  const { ref: refS3, inView: inS3 } = useInView<HTMLDivElement>();
  const { ref: refS4, inView: inS4 } = useInView<HTMLElement>();
  const { ref: refS5, inView: inS5 } = useInView<HTMLElement>();

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cmsFaq = useCmsFaq(ZAMOWIENIE_SPECJALNE_CONTENT.faq.items);
  const jsonLd = buildFaqJsonLd(cmsFaq.items);

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>

      <CmsSchema fields={cmsFaq.fields} />
      <script data-cms-jsonld="faq" data-cms-faq-prefix="information.faq.items" type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeCmsJson(jsonLd) }} />
      <PageBreadcrumb crumbs={[{ label: 'Zamówienie specjalne' }]} />

      {/* ── HERO ──────────────────────────────────────────── */}
      <section style={{ position: 'relative', minHeight: 'calc(52dvh - 45px)', display: 'flex', alignItems: 'center', background: '#fff', overflow: 'hidden' }}>

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1370, margin: '0 auto', padding: 'max(clamp(24px, 3vw, 48px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(24px, 3vw, 48px)' }}>
          <div className="bk-fade-in bk-d1" style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>

            <p className="bk-fade-up bk-d1" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', color: GOLD, marginBottom: 16 }}>
              <CmsEditorialText id="information.hero.kicker" value={ZAMOWIENIE_SPECJALNE_CONTENT.hero.kicker} section="Wstęp" label="Treść sekcji" />
            </p>


            <p className="bk-fade-up bk-d2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: 400, color: BROWN, marginBottom: 16, fontStyle: 'italic' }}>
              <CmsEditorialText id="information.hero.tagline" value={ZAMOWIENIE_SPECJALNE_CONTENT.hero.tagline} section="Wstęp" label="Nadtytuł" />
            </p>

            <h1 className="bk-fade-up bk-d3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem, 5.9vw, 5.4rem)', fontWeight: 400, lineHeight: 1.0, color: DARK, margin: '0 0 28px' }}>
              <CmsEditorialText id="information.hero.headingLine1" value={ZAMOWIENIE_SPECJALNE_CONTENT.hero.headingLine1} section="Wstęp" label="Nagłówek — pierwsza linia" /><br /><CmsEditorialText id="information.hero.headingLine2" value={ZAMOWIENIE_SPECJALNE_CONTENT.hero.headingLine2} section="Wstęp" label="Nagłówek — druga linia" /><br /><em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.hero.headingAccent" value={ZAMOWIENIE_SPECJALNE_CONTENT.hero.headingAccent} section="Wstęp" label="Wyróżniona część nagłówka" /></em>
            </h1>

            <p className="bk-fade-up bk-d4" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: BROWN, lineHeight: 1.85, maxWidth: 540, margin: '0 auto 44px' }}>
              <CmsEditorialText id="information.hero.body" value={ZAMOWIENIE_SPECJALNE_CONTENT.hero.body} section="Wstęp" label="Opis" multiline />
            </p>

            <div className="bk-fade-up bk-d5" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button onClick={scrollToForm}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '15px 32px', border: 'none', cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.opacity = '0.85')}
                onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.opacity = '1')}
              >
                <CmsEditorialText id="information.hero.ctaOrderLabel" value={ZAMOWIENIE_SPECJALNE_CONTENT.hero.ctaOrderLabel} section="Wstęp" label="Tekst przycisku" />
                <ArrowRight size={13} />
              </button>
              <a href="#jak-to-dziala"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, textDecoration: 'none', border: `1px solid ${GOLD}88`, padding: '15px 32px', transition: 'border-color 0.2s', cursor: 'pointer' }}
                onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD)}
                onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = `${GOLD}88`)}
              >
                <CmsEditorialText id="information.hero.ctaHowLabel" value={ZAMOWIENIE_SPECJALNE_CONTENT.hero.ctaHowLabel} section="Wstęp" label="Tekst przycisku" />
                <ArrowRight size={13} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── CO MOŻESZ ZAMÓWIĆ ─────────────────────────────── */}
      <section ref={refS1} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
          <div style={{ opacity: inS1 ? undefined : 0, marginBottom: 'clamp(36px,4.5vw,52px)' }} className={inS1 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={ZAMOWIENIE_SPECJALNE_CONTENT.categories.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem,3.2vw,2.9rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
              <CmsEditorialText id="information.categories.heading" value={ZAMOWIENIE_SPECJALNE_CONTENT.categories.heading} section="Rodzaje kompozycji" label="Nagłówek" />
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,240px),1fr))', gap: 1, background: LINE }}>
            {ZAMOWIENIE_SPECJALNE_CONTENT.categories.items.map((cat, i) => {
              const Icon = CATEGORY_ICON_MAP[cat.iconKey];
              return (
                <div key={cat.label} style={{ opacity: inS1 ? undefined : 0, animationDelay: `${i * 0.07}s` }} className={inS1 ? 'bk-fade-up' : undefined}>
                  <button onClick={scrollToForm} style={{ width: '100%', background: CREAM, padding: 'clamp(24px,3vw,36px)', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'block', height: '100%' }}
                    onMouseEnter={e => (e.currentTarget.style.background = CREAM3)}
                    onMouseLeave={e => (e.currentTarget.style.background = CREAM)}
                  >
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${GOLD}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                      <Icon size={16} color={GOLD} />
                    </div>
                    <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(17px,1.8vw,21px)', fontWeight: 400, color: DARK, margin: '0 0 8px', lineHeight: 1.2 }}>
                      <CmsEditorialText id={`information.categories.items.${i}.label`} value={cat.label} section="Rodzaje kompozycji" label={`Element ${i + 1}: tekst`} />
                    </h3>
                    <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, lineHeight: 1.7, margin: '0 0 14px' }}>
                      <CmsEditorialText id={`information.categories.items.${i}.desc`} value={cat.desc} section="Rodzaje kompozycji" label={`Element ${i + 1}: opis`} multiline />
                    </p>
                    <span style={{ fontFamily: 'DM Sans', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: GOLD }}>
                      <CmsEditorialText id="information.categories.ctaLabel" value={ZAMOWIENIE_SPECJALNE_CONTENT.categories.ctaLabel} section="Rodzaje kompozycji" label="Tekst przycisku" />
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── JAK TO DZIAŁA ─────────────────────────────────── */}
      <section ref={refS2} id="jak-to-dziala" style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
          <div style={{ opacity: inS2 ? undefined : 0, marginBottom: 'clamp(36px,4.5vw,52px)' }} className={inS2 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={ZAMOWIENIE_SPECJALNE_CONTENT.process.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem,3.2vw,2.9rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
              <CmsEditorialText id="information.process.heading" value={ZAMOWIENIE_SPECJALNE_CONTENT.process.heading} section="Jak to działa" label="Nagłówek" />
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,240px),1fr))', gap: 1, background: LINE }}>
            {ZAMOWIENIE_SPECJALNE_CONTENT.process.steps.map((s, i) => (
              <div key={s.n} style={{ opacity: inS2 ? undefined : 0, animationDelay: `${i * 0.08}s`, background: '#fff', padding: 'clamp(24px,3vw,40px)', borderTop: `2px solid ${i === 0 ? GOLD : 'transparent'}` }} className={inS2 ? 'bk-fade-up' : undefined}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.4rem,4vw,3.6rem)', fontWeight: 400, color: `${GOLD}28`, lineHeight: 1, margin: '0 0 20px' }}>{s.n}</p>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(16px,1.7vw,20px)', fontWeight: 400, color: DARK, margin: '0 0 12px', lineHeight: 1.3 }}><CmsEditorialText id={`information.process.steps.${i}.title`} value={s.title} section="Jak to działa" label={`Element ${i + 1}: tytuł`} /></h3>
                <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, lineHeight: 1.8, margin: 0 }}><CmsEditorialText id={`information.process.steps.${i}.note`} value={s.note} section="Jak to działa" label={`Element ${i + 1}: opis`} multiline /></p>
              </div>
            ))}
          </div>

          <div style={{ opacity: inS2 ? undefined : 0, animationDelay: '0.12s', marginTop: 36, textAlign: 'center' }} className={inS2 ? 'bk-fade-up' : undefined}>
            <button onClick={scrollToForm} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '13px 32px', border: 'none', cursor: 'pointer' }}>
              <CmsEditorialText id="information.process.ctaLabel" value={ZAMOWIENIE_SPECJALNE_CONTENT.process.ctaLabel} section="Jak to działa" label="Tekst przycisku" /> <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </section>

      {/* ── FORMULARZ ─────────────────────────────────────── */}
      <section ref={formRef} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 clamp(24px,5vw,48px)' }}>
          <div ref={refS3} style={{ opacity: inS3 ? undefined : 0, marginBottom: 'clamp(32px,4vw,44px)' }} className={inS3 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={ZAMOWIENIE_SPECJALNE_CONTENT.formIntro.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem,3.2vw,2.9rem)', fontWeight: 400, color: DARK, margin: '0 0 10px', lineHeight: 1.1 }}>
              <CmsEditorialText id="information.formIntro.heading" value={ZAMOWIENIE_SPECJALNE_CONTENT.formIntro.heading} section="Wprowadzenie formularza" label="Nagłówek" />
            </h2>
            <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.85, margin: 0 }}>
              <CmsEditorialText id="information.formIntro.body" value={ZAMOWIENIE_SPECJALNE_CONTENT.formIntro.body} section="Wprowadzenie formularza" label="Opis" multiline />
            </p>
          </div>
          <div style={{ opacity: inS3 ? undefined : 0, animationDelay: '0.08s' }} className={inS3 ? 'bk-fade-up' : undefined}>
            <SpecialOrderForm />
          </div>
        </div>
      </section>

      {/* ── NIE WIESZ CZY SIĘ DA ─────────────────────────── */}
      <section ref={refS4} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 clamp(24px,5vw,48px)', textAlign: 'center' }}>
          <div style={{ opacity: inS4 ? undefined : 0 }} className={inS4 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={ZAMOWIENIE_SPECJALNE_CONTENT.reassurance.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem,3.2vw,2.9rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.1 }}>
              <CmsEditorialText id="information.reassurance.headingLine1" value={ZAMOWIENIE_SPECJALNE_CONTENT.reassurance.headingLine1} section="Informacja dodatkowa" label="Nagłówek — pierwsza linia" /><br />
              <em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.reassurance.headingAccent" value={ZAMOWIENIE_SPECJALNE_CONTENT.reassurance.headingAccent} section="Informacja dodatkowa" label="Wyróżniona część nagłówka" /></em>
            </h2>
            <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.85, margin: '0 0 28px' }}>
              <CmsEditorialText id="information.reassurance.body" value={ZAMOWIENIE_SPECJALNE_CONTENT.reassurance.body} section="Informacja dodatkowa" label="Opis" multiline />
            </p>
            <button onClick={scrollToForm} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '13px 28px', border: 'none', cursor: 'pointer' }}>
              <CmsEditorialText id="information.reassurance.ctaLabel" value={ZAMOWIENIE_SPECJALNE_CONTENT.reassurance.ctaLabel} section="Informacja dodatkowa" label="Tekst przycisku" /> <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────── */}
      <section ref={refS5} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 clamp(24px,5vw,48px)' }}>
          <div style={{ opacity: inS5 ? undefined : 0, marginBottom: 'clamp(32px,4vw,44px)' }} className={inS5 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={ZAMOWIENIE_SPECJALNE_CONTENT.faq.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem,3.2vw,2.9rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
              <CmsEditorialText id="information.faq.heading" value={ZAMOWIENIE_SPECJALNE_CONTENT.faq.heading} section="Pytania i odpowiedzi" label="Nagłówek" />
            </h2>
          </div>
          <div style={{ opacity: inS5 ? undefined : 0, animationDelay: '0.08s' }} className={inS5 ? 'bk-fade-up' : undefined}>
            {cmsFaq.items.map((item, i) => <FaqAccordionItem key={i} q={item.q} a={item.a} cmsKey={item.cmsKey} />)}
          </div>

        </div>
      </section>

      <CategoryBuyingGuide content={REMAINING_SEO_GUIDES['zamowienie-specjalne']} />
      <Footer />
    </div>
  );
}
